import { pdf } from "@react-pdf/renderer";

import moment from "moment";
import { customAlphabet } from "nanoid/non-secure";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import CheckoutOrderList from "../../components/checkoutComponents/checkoutOrderList/CheckoutOrderList";
import PaymentMethod from "../../components/checkoutComponents/paymentMethod/PaymentMethod";
import CheckoutForm from "../../components/forms/checkoutForm/CheckouForm";
import PdfContent from "../../components/pdf/PdfContent";
import { AuthContext } from "../../services/context/authContext/AuthContext";
import { OrderContext } from "../../services/context/orderContext/OrderContext";
import { db } from "../../services/firebaseConfig";

const addressValues = {
  address: "",
  city: "",
  state: "",
  zip: "",
};

const formFieldsValues = {
  id: "",
  company: "",
  createdAt: "",
  createdBy: "",
  isPickup: null,
  deliveryDate: undefined,
  pickupDate: undefined,
  isDelivery: false,
  isTaxable: false,
  isAssemble: false,
  isTouchUpKit: false,
  salesTax: 0,
};

const dashboardValues = {
  cabinetAssembleFee: 0,
  customCabinetAssembleFee: 0,
  discount: 0,
  email: "",
  pantryAssembleFee: 0,
  touchUpKit: 0,
};
const Checkout = () => {
  const navigate = useNavigate();
  const order = useContext(OrderContext);
  const loggedInUser = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(formFieldsValues);
  const [billingAddress, setBillingAddress] = useState(addressValues);
  const [deliveryAddress, setDeliveryAddress] = useState(addressValues);
  const [isPayment, setIsPayment] = useState(false);
  const [dashboardVariables, setDashboardVariables] = useState(dashboardValues);
  const [subtotal, setSubtotal] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [assemblePrice, setAssemblePrice] = useState(0);
  const [xlsxProductsArray, setXlsxProductsArray] = useState([]);

  const [deliveryIsBilling, setDeliveryIsBilling] = useState(false);
  const [salesTax, setSalesTax] = useState(0);

  const ChicagoSalesTax = 10.25;

  // ----------------------- effect for calculate total products price -----------------------

  useEffect(() => {
    let price = 0;

    for (let product of order.currentOrder) {
      price = price + product.qty * product.price;
    }

    return setSubtotal(price);
  }, [subtotal, order, navigate]);

  // ----------------------- effect for calculate total order price -----------------------
  useEffect(() => {
    if (!order.currentOrder.length) {
      return;
    }

    const total =
      subtotal +
      (formValues.isAssemble ? assemblePrice : 0) +
      (formValues.isTouchUpKit ? dashboardVariables?.touchUpKit : 0);

    if (
      deliveryAddress.city === "Chicago" &&
      deliveryAddress.state === "Illinois"
    ) {
      const calculatedPrice = formValues.isTaxable
        ? total + total * (ChicagoSalesTax / 100)
        : total;
      setSalesTax(total * (ChicagoSalesTax / 100));
      return setTotalPrice(calculatedPrice);
    } else {
      const calculatedPrice = formValues.isTaxable
        ? total + total * (formValues.taxRate / 100)
        : total;
      setSalesTax(total * (formValues.taxRate / 100));
      return setTotalPrice(calculatedPrice);
    }
  }, [
    order.currentOrder.length,
    subtotal,
    formValues.isTouchUpKit,
    formValues.isAssemble,
    formValues.isTaxable,
    assemblePrice,
    dashboardVariables?.touchUpKit,
    formValues.taxRate,
    deliveryAddress,
    billingAddress,
  ]);
  useEffect(() => {
    if (!order.currentOrder.length) {
      return;
    }
    const isAdmin = loggedInUser?.role.toLowerCase() === "admin";
    let productList = [];
    let adminDiscount = 1;
    try {
      const getAdminDiscount = async () => {
        const dashboardVariables = await getDoc(
          doc(db, "dashboard", "dashboardVariables")
        );

        if (dashboardVariables.exists()) {
          const data = dashboardVariables.data();
          setDashboardVariables(data);
          adminDiscount = data?.discount;
        }
      };
      getAdminDiscount();

      const userDiscount = loggedInUser?.discount;
      order.currentOrder.map((product) => {
        const {
          qty,
          sku,
          description,
          hinge,
          weight,
          cubes,
          price,
          type,
          isCustom,
          drawer,
        } = product;
        //get original item price before discount is applied
        const fullPrice = (Number(price) * 100) / (100 - Number(userDiscount));
        //apply admin discount on original price for excel list if user is admin Admin discounted price equals price
        const adminDiscountPrice = isAdmin
          ? price
          : Number(fullPrice) -
            (Number(fullPrice) * Number(adminDiscount)) / 100;
        const customSize = isCustom ? "Yes" : "-";
        const hasDrawer = drawer === "Yes" ? "Yes" : "-";
        const productData = [
          qty,
          sku,
          description,
          hinge,
          weight,
          cubes,
          adminDiscountPrice,
          type.split(" ")[0],
          customSize,
          hasDrawer,
        ];
        return productList.push(productData);
      });

      setXlsxProductsArray(productList);
    } catch (error) {
      console.log(error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.currentOrder]);

  const handleEncodedPdfXlsx = async (orderID, invoiceId) => {
    // ---------------------------encode data to base64  PDF-------------------
    const instance = pdf();
    const combineValues = {
      invoiceId: invoiceId,
      id: orderID,
      products: order.currentOrder,
      subtotal: subtotal,
      totalPrice: totalPrice,
      assembleFee: assemblePrice,
      touchUpKit: Number(dashboardVariables.touchUpKit),
      billingAddress: deliveryIsBilling ? deliveryAddress : billingAddress,
    };
    const pdfObject = { ...formValues, ...combineValues, salesTax: salesTax };

    pdfObject.billingAddress = deliveryIsBilling
      ? deliveryAddress
      : billingAddress;

    instance.updateContainer(<PdfContent order={pdfObject} />);
    const blob = await instance.toBlob();
    const reader = new window.FileReader();
    reader.readAsDataURL(blob);
    const pdfEncode = await new Promise((resolve) => {
      reader.onloadend = () => resolve(reader.result);
    });

    // xlsx encode
    const { state, address, zip, city } = deliveryAddress;
    let XlData = [
      ["Address"],
      [address + ", " + city + ", " + state + ", " + zip],
      [
        "Quantity",
        "SKU",
        "Description",
        "Hinging",
        "Weight",
        "Cubes",
        "Price",
        "Style",
        "Custom size",
        "Drawer",
      ],
      ...xlsxProductsArray,
    ];
    const ws = XLSX.utils.aoa_to_sheet(XlData);
    const wb = XLSX.utils.book_new();
    ws["!cols"] = [
      { wch: 10 },
      { wch: 12 },
      { wch: 60 },
      { wch: 10 },
      { wch: 8 },
      { wch: 7 },
      { wch: 14 },
    ];
    ws["!merges"] = [
      // Set the cell merge of A1-G1
      //  r = row c = column
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    ];
    // style available only in pro version
    ws["A1"].s = {
      font: {
        sz: 13,
        bold: true,
        color: {
          rgb: "FFFFAA00",
        },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrap_text: true,
      },
    };
    //height of rows
    ws["!rows"] = [{ hpx: 20 }];

    XLSX.utils.book_append_sheet(wb, ws, "products");
    /* generate base64 string */
    const encodedData = await XLSX.write(wb, { type: "base64" });

    return {
      xlsx: encodedData,
      pdf: pdfEncode,
    };
  };

  const handleConfirmForm = async () => {
    if (order.currentOrder.length > 0) {
      setIsPayment(true);
    } else alert("Please add products to your order.");
  };
  const handleConfirmOrder = async () => {
    if (
      !deliveryIsBilling &&
      !billingAddress?.address &&
      !billingAddress.state &&
      !billingAddress.zip &&
      !billingAddress.city
    ) {
      alert("complete all billing form fields!");
    } else {
      return postOrder("complete", "cash");
    }
  };
  const postOrder = async (status, paymentType) => {
    setIsLoading(true);

    const nanoid = customAlphabet("1234567890abcdefghijklmnopqrstuvxz", 7);
    const invoiceId = nanoid();

    if (formValues) {
      setIsLoading(true);
      const doc_ref = doc(collection(db, "orders"));

      return await handleEncodedPdfXlsx(doc_ref.id, invoiceId).then(
        async (data) => {
          const delivery = formValues.deliveryDate
            ? moment(formValues.deliveryDate).format("MMMM DD YYYY")
            : moment(formValues.pickupDate).format("MMMM DD YYYY");
          return await setDoc(doc_ref, {
            status: status,
            createdBy: loggedInUser?.email,
            id: doc_ref.id,
            invoiceId: invoiceId,
            createdAt: serverTimestamp(),
            company: formValues.company,
            billingAddress: deliveryIsBilling
              ? deliveryAddress
              : billingAddress,
            deliveryAddress: deliveryAddress,
            deliveryDate: formValues.deliveryDate ? delivery : null,
            pickupDate: formValues.pickupDate ? delivery : null,
            totalPrice: totalPrice,
            subtotal: subtotal,
            isTaxable: formValues.isTaxable,
            salesTax: formValues.isTaxable ? salesTax : 0,
            isAssemble: formValues.isAssemble,
            isTouchUpKit: formValues.isTouchUpKit,
            touchUpKit: formValues.isTouchUpKit
              ? Number(dashboardVariables.touchUpKit)
              : 0,
            assembleFee: formValues.isAssemble ? assemblePrice : 0,
            products: order.currentOrder,
            encodedPdf: data.pdf,
            encodedXlsx: data.xlsx,
            isPayed: false,
            paymentType: paymentType,
          })
            .then(() => {
              if (status === "complete") {
                order.updateOrder([]);
                setIsLoading(false);
                navigate("/confirm", { state: { orderId: doc_ref.id } });
              } else {
                return { orderId: doc_ref.id, orderNr: invoiceId };
              }
            })
            .catch((err) => {
              console.log(err);
              setIsLoading(false);
            });
        }
      );
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto">
      <div className=" px-5 py-5 tablet:px-10 tablet:py-10 xl:px-24">
        <h1 className="">Checkout</h1>
        {isPayment && (
          <h2 className={"mt-10 cursor-pointer"}>
            <span
              onClick={() => setIsPayment(false)}
              className={"text-txtGray"}
            >
              Checkout
            </span>
            /Payment
          </h2>
        )}
        <div className="flex flex-col desktop:flex-row w-full gap-5 xl:gap-10 mt-6 tablet:mt-12">
          {!isPayment ? (
            <CheckoutForm
              formFields={formValues}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              setFormFields={setFormValues}
              confirmOrder={handleConfirmForm}
              isLoading={isLoading}
            />
          ) : (
            <PaymentMethod
              isLoading={isLoading}
              deliveryIsBilling={deliveryIsBilling}
              setDeliveryIsBilling={setDeliveryIsBilling}
              address={billingAddress}
              setAddress={setBillingAddress}
              onSubmit={handleConfirmOrder}
            />
          )}
          <CheckoutOrderList
            isAssemble={formValues.isAssemble}
            isTouchUpKit={formValues.isTouchUpKit}
            isTaxable={formValues.isTaxable}
            assemblePrice={assemblePrice}
            dashboardVariables={dashboardVariables}
            setAssemblePrice={setAssemblePrice}
            subtotal={subtotal}
            salesTax={salesTax}
            totalPrice={totalPrice}
          />
        </div>
      </div>
    </div>
  );
};

export default Checkout;
